import React from "react";
import { useTranslation } from "react-i18next";
import { PackingSlipModel } from "../../../dailyPlanning/domain/models/packingSlipModel";
import { Page, Text, View, StyleSheet, Image } from "@react-pdf/renderer";
import moment from "moment";
import LicensePlate from "license-plate";
import { RequestDealerModel } from "../../../dailyPlanning/domain/models/dealerModel";

interface props {
  packingSlip: PackingSlipModel;
}

export const PackingSlipPdfPage = ({ packingSlip }: props) => {
  const { t } = useTranslation("common");

  const deliveryRequests = packingSlip.requests.filter(
    (request) => request.type == "deliver",
  );

  const pickupRequests = packingSlip.requests.filter(
    (request) => request.type == "pickup",
  );

  // Function to chunk array into groups of specified size.
  const requestLineCount = (request: RequestDealerModel): number => {
    return 1 + (request.work_order_number ? 1 : 0);
  };

  const chunkRequestsByLines = (
    requests: RequestDealerModel[],
    maxLinesPerChunk: number,
  ): RequestDealerModel[][] => {
    const chunks: RequestDealerModel[][] = [];
    let currentChunk: RequestDealerModel[] = [];
    let usedLines = 0;

    for (const req of requests) {
      const lines = requestLineCount(req);

      if (lines > maxLinesPerChunk) {
        if (currentChunk.length) {
          chunks.push(currentChunk);
          currentChunk = [];
          usedLines = 0;
        }
        chunks.push([req]);
        continue;
      }

      if (usedLines + lines > maxLinesPerChunk) {
        if (currentChunk.length) {
          chunks.push(currentChunk);
        }
        currentChunk = [req];
        usedLines = lines;
      } else {
        currentChunk.push(req);
        usedLines += lines;
      }
    }

    if (currentChunk.length) {
      chunks.push(currentChunk);
    }

    return chunks;
  };

  // Function to render request item
  const renderRequest = (request: RequestDealerModel) => {
    if (request.source && "car" in request.source) {
      const licencePlate = new LicensePlate(request.source.car?.licence || "");

      return (
        <View key={request.id}>
          <View style={styles.dataSection} break={false}>
            <Text>
              {licencePlate.valid()
                ? licencePlate.pretty()
                : request.source.car?.licence}

              {request.source.car?.label ?? t("deleted_or_unknown")}
            </Text>
            <Text>&nbsp;</Text>
            <Text>{t(request.source.type + "")}</Text>
          </View>
          {request.work_order_number && (
            <View style={styles.workOrderSection}>
              <Text>
                {t("W/O Number")}: {request.work_order_number}
              </Text>
            </View>
          )}
        </View>
      );
    }
    return null;
  };

  // Function to render a column of requests
  const renderRequestColumn = (
    requests: RequestDealerModel[],
    title: string,
    showPickupAmount = false,
  ) => {
    const requestChunks = chunkRequestsByLines(requests, 35);

    return requestChunks.map((chunk, chunkIndex) => (
      <View key={`${title}-${chunkIndex}`} style={styles.columnSection}>
        {chunkIndex === 0 ? (
          <>
            <Text style={styles.textHeading}>{title}</Text>
            {showPickupAmount && (
              <Text>
                {t("Pickup amount")}: {packingSlip.pickup_amount ?? 0}
              </Text>
            )}
          </>
        ) : (
          <>
            <Text style={styles.textHeading}>&nbsp;</Text>
            {showPickupAmount && <Text>&nbsp;</Text>}
          </>
        )}
        {chunk.map((request) => renderRequest(request))}
      </View>
    ));
  };

  return (
    <Page size="A4" style={styles.page}>
      {/* Header information - fixed on first page */}
      <View style={styles.layout} fixed>
        <View style={[styles.section, styles.addressSection]}>
          <Text style={styles.textHeading}>{packingSlip.relation?.name}</Text>
          <Text>
            {packingSlip.relation?.street} {packingSlip.relation?.house_number}{" "}
            {packingSlip.relation?.house_number_addition}
          </Text>
          <Text>{packingSlip.relation?.postal_code}</Text>
          <Text>{packingSlip.relation?.city}</Text>
          <Text>{packingSlip.relation?.phone}</Text>
        </View>
        <View style={[styles.section, styles.informationSection]}>
          <Text>
            {t("Packing slip")}: {packingSlip.id}
          </Text>
          <Text>
            {t("Date")}: {moment(packingSlip.date).format("DD-MM-YYYY")}
          </Text>
          <Text>
            {t("Truck")}:{" "}
            {new LicensePlate(packingSlip.route?.truck?.licence ?? "").pretty()}
          </Text>
          <Text>
            {t("Route number")}: {(packingSlip.route?.delta ?? 0) + 1}
          </Text>
          {packingSlip.comment && (
            <Text>
              {t("Comment")}: {packingSlip.comment}
            </Text>
          )}
          <Text>
            {t("Status")}: {t(packingSlip.status ?? "")}
          </Text>
        </View>
      </View>

      {/* Content arranged in columns */}
      {deliveryRequests.length < 35 && pickupRequests.length < 35 ? (
        <View style={styles.layout}>
          {deliveryRequests.length > 0 &&
            renderRequestColumn(deliveryRequests, t("Deliver requests"))}
          {pickupRequests.length > 0 &&
            renderRequestColumn(pickupRequests, t("Pickup requests"), true)}
        </View>
      ) : (
        <>
          <View style={styles.layout}>
            {deliveryRequests.length > 0 &&
              renderRequestColumn(deliveryRequests, t("Deliver requests"))}
          </View>
          <View style={styles.layout} wrap={false}>
            {pickupRequests.length > 0 &&
              renderRequestColumn(pickupRequests, t("Pickup requests"), true)}
          </View>
        </>
      )}

      {packingSlip.signature_time && (
        <View style={styles.layout}>
          <View style={[styles.section]}>
            <Text style={styles.textHeading}>{t("Signature")}</Text>
            <Text>
              {moment(packingSlip.signature_time).format("DD-MM-YYYY h:mm")}
            </Text>
            <Image src={packingSlip.signature} style={styles.signature} />
          </View>
        </View>
      )}
    </Page>
  );
};

const styles = StyleSheet.create({
  page: {
    flexDirection: "column",
    marginTop: 120,
    fontSize: 12,
    paddingBottom: 120,
    paddingLeft: 30,
    paddingRight: 30,
  },
  layout: {
    flexDirection: "row",
  },
  section: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
  },
  columnSection: {
    margin: 10,
    padding: 10,
    flexGrow: 1,
    minWidth: 0, // Allow column to shrink
  },
  addressSection: {
    paddingTop: 20,
    fontSize: 14,
  },
  informationSection: {
    marginTop: "auto",
  },
  textHeading: {
    fontWeight: "bold",
  },
  dataSection: {
    flexDirection: "row",
  },
  signature: {
    width: 200,
  },
  workOrderSection: {
    maxWidth: 180,
    marginLeft: 5,
    color: "#7e8299",
  },
});
